import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import authService from '../services/auth.service'
import { initializeEcho, disconnectEcho } from '../services/echo'
import fcmService from '../services/fcm.service'
import { useToastStore } from './toast'
import router from '../router'
import i18n from '../i18n'
import type { User, LoginCredentials, RegisterData, UserInitResponse } from '../types/auth.types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const initData = ref<UserInitResponse | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const isDemo = ref(false)
  const demoStatus = ref<'pending' | 'active' | 'expired' | null>(null)
  const showDemoCredentialsSent = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const currentUser = computed(() => user.value)
  const userInitData = computed(() => initData.value)

  // Push notification foreground handler — show toast when notification arrives while app is open
  const handleForegroundNotification = (title: string, body: string, _data: Record<string, string>) => {
    const toast = useToastStore()
    const message = title ? `${title}: ${body}` : body
    if (message) {
      toast.success(message, 6000)
    }
  }

  // Navigate on notification click (native Capacitor)
  const handleNotificationClick = (url: string) => {
    router.push(url)
  }

  // Initialize push notifications (non-blocking)
  const initPushNotifications = () => {
    fcmService.initializePushNotifications(handleForegroundNotification, handleNotificationClick).catch((err) => {
      console.warn('Push notifications init failed:', err)
    })
  }

  // Actions
  const initAuth = () => {
    // Load user, token and init data from localStorage on app start
    const storedToken = authService.getToken()
    const storedUser = authService.getUser()
    const storedInitData = authService.getInitData()

    if (storedToken && storedUser) {
      token.value = storedToken
      user.value = storedUser
      initData.value = storedInitData
      isDemo.value = storedUser.is_demo ?? false
      demoStatus.value = storedUser.demo_status ?? null

      // Synchronize language from stored init data
      if (storedInitData?.lang) {
        const userLang = storedInitData.lang
        // Update i18n locale
        i18n.global.locale.value = userLang
        // Update localStorage to match
        localStorage.setItem('language', userLang)
      }

      // Initialize Echo for real-time messaging
      initializeEcho(storedToken)

      // Initialize push notifications
      initPushNotifications()
    }
  }

  const login = async (credentials: LoginCredentials) => {
    loading.value = true
    error.value = null

    try {
      const response = await authService.login(credentials)

      token.value = response.access_token
      user.value = response.user

      // Initialize user data immediately after login
      if (response.user?.uuid) {
        await initUserData(response.user.uuid)
      }

      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || (i18n.global.t as (key: string) => string)('common.errorLogin')
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Initialize user data from /api/v1/{user}/init endpoint
   */
  const initUserData = async (userUuid: string) => {
    try {
      const response = await authService.initUser(userUuid)
      initData.value = response

      // Update user if present in response
      if (response.user) {
        user.value = response.user
      }

      // Synchronize language from backend with frontend
      if (response.lang) {
        const userLang = response.lang
        // Update i18n locale
        i18n.global.locale.value = userLang
        // Update localStorage to match backend
        localStorage.setItem('language', userLang)
      }

      // Initialize Echo for real-time messaging
      if (token.value) {
        initializeEcho(token.value)
      }

      // Initialize push notifications
      initPushNotifications()

      return response
    } catch (err: any) {
      console.error('Init user data error:', err)
      error.value = err.response?.data?.message || (i18n.global.t as (key: string) => string)('common.errorInit')
      throw err
    }
  }

  const register = async (data: RegisterData) => {
    loading.value = true
    error.value = null

    try {
      const response = await authService.register(data)
      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || (i18n.global.t as (key: string) => string)('common.errorRegister')
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    loading.value = true
    error.value = null

    try {
      await authService.logout()
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      // Deactivate FCM token
      await fcmService.removeToken()
      fcmService.cleanup()

      // Disconnect Echo
      disconnectEcho()

      token.value = null
      user.value = null
      initData.value = null
      isDemo.value = false
      demoStatus.value = null
      loading.value = false
    }
  }

  const setDemoToken = (demoToken: string, demoUser: User) => {
    authService.setToken(demoToken)
    authService.setUser(demoUser)
    token.value = demoToken
    user.value = demoUser
    isDemo.value = demoUser.is_demo ?? true
    demoStatus.value = 'pending'
  }

  const finalizeDemoActivation = async () => {
    demoStatus.value = 'active'
    if (user.value?.uuid) {
      await initUserData(user.value.uuid)
    }
  }

  const fetchCurrentUser = async () => {
    if (!token.value) return

    loading.value = true
    error.value = null

    try {
      const userData = await authService.getCurrentUser()
      user.value = userData
      isDemo.value = userData.is_demo ?? false
      demoStatus.value = userData.demo_status ?? null
    } catch (err: any) {
      error.value = err.response?.data?.message || (i18n.global.t as (key: string) => string)('common.errorFetchingUser')
      // If 401, clear auth data
      if (err.response?.status === 401) {
        token.value = null
        user.value = null
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  return {
    // State
    user,
    token,
    initData,
    loading,
    error,
    isDemo,
    demoStatus,
    showDemoCredentialsSent,
    // Getters
    isAuthenticated,
    currentUser,
    userInitData,
    // Actions
    initAuth,
    login,
    initUserData,
    register,
    logout,
    fetchCurrentUser,
    setDemoToken,
    finalizeDemoActivation,
    clearError
  }
})
